import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import CtaBand from "@/components/CtaBand";
import CommitmentSlider from "@/components/CommitmentSlider";
import { SERVICES } from "@/lib/data";

const ACCENT_TOKENS = {
  navy: { bg: "var(--zt-navy)", soft: "rgba(30, 58, 138, 0.10)" },
  gold: { bg: "var(--zt-teal)", soft: "rgba(51, 151, 172, 0.12)" },
  sky: { bg: "var(--zt-teal)", soft: "rgba(51, 151, 172, 0.12)" },
};

// Single unified service section — editorial, list-based, no random squares.
function ServiceSection({ s, idx }) {
  const a = ACCENT_TOKENS[s.accent];
  const reversed = idx % 2 === 1;

  return (
    <section
      id={s.slug}
      data-testid={`service-${s.slug}`}
      className="zt-section border-t border-slate-200"
      style={idx % 2 === 1 ? { background: "var(--zt-bg-alt)" } : {}}
    >
      <div className="zt-container">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
          {/* LEFT: Title block + image */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="zt-overline mb-5">{s.eyebrow}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-6">
              {s.title}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">{s.intro}</p>

            <div className="relative">
              <img src={s.image} alt={s.title} className="w-full aspect-[4/3] object-cover" />
              {/* Thin diagonal accent stripe */}
              <div
                className="absolute -bottom-3 left-6 right-6 h-1"
                style={{ background: a.bg }}
              />
            </div>
          </div>

          {/* RIGHT: Editorial list of points + bottom CTA */}
          <div className="lg:col-span-7">
            <ul className="divide-y divide-slate-200 border-y border-slate-200">
              {s.points.map((p) => {
                return (
                  <li
                    key={p.title}
                    data-testid={`service-point-${s.slug}-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group py-8 lg:py-10 flex gap-6 md:gap-8 items-start hover:pl-2 transition-[padding] duration-300"
                  >
                    {/* Vertical color line accent */}
                    <span
                      className="flex-shrink-0 block w-[3px] h-12 transition-all duration-300 group-hover:h-16"
                      style={{ background: a.bg }}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg md:text-xl font-semibold mb-2 leading-tight">{p.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{p.description}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      {/* SERVICES HERO — image-led, like Home */}
      <section data-testid="services-hero" className="relative overflow-hidden" style={{ background: "var(--zt-bg-alt)" }}>
        <div className="zt-container pt-8 md:pt-20 pb-10 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="zt-overline mb-4">Services</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              A multi-vertical healthcare platform
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-5 md:mt-6 max-w-2xl">
              Three integrated service verticals — freestanding emergency rooms, micro-hospitals, and physician staffing — designed to expand access and operate scalable, community-based care.
            </p>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/4cig40rw_iStock-1321691592.webp"
                alt="ZT Health nurse consulting with patient"
                className="w-full h-[240px] sm:h-[320px] lg:h-[480px] object-cover"
                loading="eager"
              />
              <div className="absolute top-0 right-0 w-12 md:w-16 h-12 md:h-16" style={{ background: "var(--zt-teal)" }} />
              <div className="absolute bottom-0 left-0 w-20 md:w-24 h-2" style={{ background: "var(--zt-navy)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Service nav strip */}
      <div className="border-b border-slate-200 bg-white sticky top-20 z-30 backdrop-blur-sm" data-testid="services-quick-nav">
        <div className="zt-container flex flex-wrap gap-x-2 gap-y-1 py-3">
          {SERVICES.map((s) => (
            <a
              key={s.slug}
              href={`#${s.slug}`}
              className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-900 transition-colors"
            >
              {s.title}
            </a>
          ))}
        </div>
      </div>

      {SERVICES.map((s, i) => (
        <ServiceSection key={s.slug} s={s} idx={i} />
      ))}

      {/* Our Commitment — moved here from Locations */}
      <section data-testid="services-commitment" className="zt-section border-t border-slate-200" style={{ background: "var(--zt-bg-alt)" }}>
        <div className="zt-container">
          <div className="max-w-3xl mb-12">
            <p className="zt-overline mb-5">Our Commitment</p>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              The principles guiding every service we deliver
            </h2>
          </div>
          <CommitmentSlider />
        </div>
      </section>

      <CtaBand
        heading="Partner with a proven operating platform"
        subtext="Whether you're an investor, a physician group, or a healthcare operator, let's explore how ZT Health's platform can support your goals."
      />
    </div>
  );
}
